import { api } from '@/api';
import type { DriveStatus, StoredFile } from '@/api/types';

export const DRIVE_QUERY_KEY = ['drive', 'status'] as const;
export const DRIVE_MESSAGE = 'app:drive-connection';

export interface DriveResult {
  status: 'connected' | 'error';
  code?: string | null;
}

export function getDriveStatus(signal?: AbortSignal) {
  return api.get<DriveStatus>('/integrations/google/drive', { signal });
}

/**
 * Asks Google for Drive access (incremental: only `drive.file`, only now).
 * Uses a popup so the current page (e.g. a photo being attached) survives;
 * falls back to a full redirect when popups are blocked.
 */
export async function connectDrive(returnPath: string): Promise<DriveResult> {
  const { authorizationUrl } = await api.post<{ authorizationUrl: string }>(
    '/integrations/google/drive/connect',
    { returnPath },
  );
  const popup = window.open(authorizationUrl, 'google-drive', 'popup,width=520,height=680');
  if (!popup) {
    window.location.assign(authorizationUrl);
    return new Promise(() => {});
  }
  return new Promise((resolve) => {
    const onMessage = (event: MessageEvent) => {
      // Only our own origin (the completion page is served by this host).
      if (event.origin !== window.location.origin || event.data?.type !== DRIVE_MESSAGE) return;
      cleanup();
      resolve({ status: event.data.status, code: event.data.code });
    };
    const timer = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        resolve({ status: 'error', code: 'cancelled' });
      }
    }, 500);
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
    };
    window.addEventListener('message', onMessage);
  });
}

export function connectDevStorage() {
  return api.post<DriveStatus>('/integrations/dev-storage/connect', {});
}

export function disconnectDrive() {
  return api.delete<void>('/integrations/google/drive');
}

export function uploadFile(
  file: Blob,
  category: string,
  related?: { type: string; id: string },
): Promise<StoredFile> {
  const form = new FormData();
  form.append('file', file, file instanceof File ? file.name : 'upload');
  form.append('category', category);
  if (related) {
    form.append('relatedEntityType', related.type);
    form.append('relatedEntityId', related.id);
  }
  return api.post<StoredFile>('/files', form, { timeoutMs: 120_000 });
}

/** Fetches a private file with the session token and returns an object URL. */
export async function fileObjectUrl(fileId: string): Promise<string> {
  const response = await api.request<Blob>('GET', `/files/${fileId}/content`, {
    parse: 'blob',
  });
  return URL.createObjectURL(response);
}

export const DRIVE_ERROR_MESSAGES: Record<string, string> = {
  cancelled: 'A conexão com o Google Drive foi cancelada.',
  'drive-scope-missing':
    'Para guardar seus arquivos precisamos da permissão de criar arquivos no seu Drive.',
  'invalid-state': 'O link de autorização expirou. Tente conectar de novo.',
  'google-grant-revoked': 'A autorização do Google expirou. Tente de novo.',
};
